// Essential-only mode for this app (youth spec v1.1; see @repo/ui/lib/signup-age). Entering it stops GA4 from loading or sending (GA's own
// `ga-disable-<id>` switch, honoured by gtag.js even when the tag is already on the page), keeps every advertising tag off (the stored
// advertising choice reads as refused while it holds), and expires this site's own GA / advertising identifier cookies. It records nothing
// about the visitor: no age, no birth date, no cohort cookie.
import type { AgeMode } from "@repo/ui/lib/signup-age";
import { ageModeOf, ANALYTICS_IDENTIFIER_COOKIE, expiryWrites, nonEssentialSuppressed, suppressNonEssential, youthAdsOff } from "@repo/ui/lib/signup-age";

export const GA_ID = "G-YMTRKT89WF";

export function isEssentialOnly(): boolean {
  return typeof document !== "undefined" && nonEssentialSuppressed(document.cookie);
}

export function enterEssentialOnlyMode(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  suppressNonEssential();
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
  for (const row of document.cookie.split(/;\s*/)) {
    const name = row.slice(0, Math.max(0, row.indexOf("=")));
    if (ANALYTICS_IDENTIFIER_COOKIE.test(name)) for (const write of expiryWrites(name, window.location.hostname)) document.cookie = write;
  }
}

/**
 * Resolves the session's band from the server (same-origin /api/auth/age, which verifies every session candidate and sets or clears the youth
 * flag) BEFORE any tag may run. Only a signed-out answer or a verified adult/unknown-age band leaves the existing contract in place. Youth,
 * and anything unresolved (a session whose band the backend could not give, two identities, an error or an unreadable answer), switch this
 * page to essential-only: failing closed costs one page of measurement, never a youth's tracking. A left-over youth flag also keeps it on.
 */
export async function resolveAgeMode(): Promise<AgeMode> {
  let mode: AgeMode = "unresolved";
  try {
    const res = await fetch("/api/auth/age", { cache: "no-store" });
    mode = res.ok ? ageModeOf(await res.json()) : "unresolved";
  } catch {
    mode = "unresolved";
  }
  if (mode === "open" && typeof document !== "undefined" && youthAdsOff(document.cookie)) mode = "youth";
  if (mode !== "open") enterEssentialOnlyMode();
  return mode;
}
